
import { useState, useCallback } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { Tier1Tracking } from '@/services/analytics/Tier1Tracking';
import { Tag } from './useTags';

export const useContentTags = (contentId: string) => {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [isLoading, setIsLoading] = useState(false);

  // Fetch tags for a specific content item
  const { data: contentTags = [], isLoading: isLoadingTags } = useQuery({
    queryKey: ['contentTags', contentId],
    queryFn: async () => {
      if (!user || !contentId) return [];
      
      const { data, error } = await supabase
        .from('content_tags')
        .select('tags:tag_id(id, name, color)')
        .eq('content_id', contentId);
      
      if (error) {
        console.error('Error fetching content tags:', error);
        return [];
      }
      
      // Properly extract and transform the nested tag data
      return data.map(item => {
        const tag = item.tags as any;
        return {
          id: tag.id,
          name: tag.name,
          color: tag.color || '#8E9196' // Default color if none is specified
        } as Tag;
      });
    },
    enabled: !!user && !!contentId,
  });

  // Add a tag to a content item
  const addTag = useCallback(async (tagId: string) => {
    if (!user || !contentId) return false;
    
    setIsLoading(true);
    try {
      console.log(`[useContentTags] === STARTING ADD TAG PROCESS ===`);
      console.log(`[useContentTags] Adding tag ${tagId} to content ${contentId}`);
      
      // Check if the tag is already applied
      const { data: existing, error: checkError } = await supabase
        .from('content_tags')
        .select('*')
        .eq('content_id', contentId)
        .eq('tag_id', tagId)
        .maybeSingle();
      
      if (checkError) {
        console.error('Error checking existing tag:', checkError);
        toast.error('Failed to add tag');
        return false;
      }
      
      // If already exists, don't add again
      if (existing) {
        toast.info('Tag already applied');
        return true;
      }
      
      const { error } = await supabase
        .from('content_tags')
        .insert([{ content_id: contentId, tag_id: tagId }]);
      
      if (error) {
        console.error('Error adding tag to content:', error);
        toast.error('Failed to add tag');
        return false;
      }
      
      console.log(`[useContentTags] === TAG ADDED TO DATABASE SUCCESSFULLY ===`);
      console.log(`[useContentTags] About to call analytics tracking...`);
      
      // ANALYTICS: Track content organization - tagged
      try {
        console.log(`[useContentTags] Calling Tier1Tracking.trackContentOrganized...`);
        console.log(`[useContentTags] Parameters:`, {
          contentId,
          action: 'tagged',
          targetId: tagId,
          targetType: 'tag',
          user: user ? `${user.id} (${user.email})` : 'None'
        });
        
        await Tier1Tracking.trackContentOrganized(
          contentId,
          'tagged',
          tagId,
          'tag',
          user
        );
        console.log(`[useContentTags] === ANALYTICS TRACKING COMPLETED SUCCESSFULLY ===`);
      } catch (analyticsError) {
        console.error(`[useContentTags] === ANALYTICS TRACKING FAILED ===`);
        console.error(`[useContentTags] Analytics error:`, analyticsError);
        // Don't fail the whole operation if analytics fails
      }
      
      queryClient.invalidateQueries({ queryKey: ['contentTags', contentId] });
      toast.success('Tag added successfully');
      
      console.log(`[useContentTags] === ADD TAG PROCESS COMPLETED ===`);
      return true;
    } catch (error) {
      console.error('Error adding tag to content:', error);
      toast.error('Failed to add tag');
      return false;
    } finally {
      setIsLoading(false);
    }
  }, [user, contentId, queryClient]);

  // Remove a tag from a content item
  const removeTag = useCallback(async (tagId: string) => {
    if (!user || !contentId) return false;
    
    setIsLoading(true);
    try {
      console.log(`[useContentTags] === STARTING REMOVE TAG PROCESS ===`);
      console.log(`[useContentTags] Removing tag ${tagId} from content ${contentId}`);
      
      const { error } = await supabase
        .from('content_tags')
        .delete()
        .eq('content_id', contentId)
        .eq('tag_id', tagId);
      
      if (error) {
        console.error('Error removing tag from content:', error);
        toast.error('Failed to remove tag');
        return false;
      }
      
      console.log(`[useContentTags] === TAG REMOVED FROM DATABASE SUCCESSFULLY ===`);
      console.log(`[useContentTags] About to call analytics tracking...`);
      
      // ANALYTICS: Track content organization - untagged
      try {
        console.log(`[useContentTags] Calling Tier1Tracking.trackContentOrganized for untagging...`);
        console.log(`[useContentTags] Parameters:`, {
          contentId,
          action: 'untagged',
          targetId: tagId,
          targetType: 'tag',
          user: user ? `${user.id} (${user.email})` : 'None'
        });
        
        await Tier1Tracking.trackContentOrganized(
          contentId,
          'untagged',
          tagId,
          'tag',
          user
        );
        console.log(`[useContentTags] === ANALYTICS TRACKING COMPLETED SUCCESSFULLY ===`);
      } catch (analyticsError) {
        console.error(`[useContentTags] === ANALYTICS TRACKING FAILED ===`);
        console.error(`[useContentTags] Analytics error:`, analyticsError);
        // Don't fail the whole operation if analytics fails
      }
      
      queryClient.invalidateQueries({ queryKey: ['contentTags', contentId] });
      toast.success('Tag removed successfully');
      
      console.log(`[useContentTags] === REMOVE TAG PROCESS COMPLETED ===`);
      return true;
    } catch (error) {
      console.error('Error removing tag from content:', error);
      toast.error('Failed to remove tag');
      return false;
    } finally {
      setIsLoading(false);
    }
  }, [user, contentId, queryClient]);

  return {
    contentTags,
    isLoading: isLoading || isLoadingTags,
    addTag,
    removeTag
  };
};

// New utility function to fetch content items that have specific tags
export const fetchContentWithTags = async (userId: string, tagIds: string[], logicType: "AND" | "OR" = "AND") => {
  if (!userId || !tagIds.length) return [];
  
  try {
    console.log(`Fetching content with tags: ${tagIds.join(', ')} using ${logicType} logic`);
    
    if (logicType === "AND") {
      // For AND logic, we need to find content that has ALL the specified tags
      // First, fetch all content items that have any of the specified tags
      const { data: contentTagsData, error: contentTagsError } = await supabase
        .from('content_tags')
        .select('content_id, tag_id')
        .in('tag_id', tagIds);
      
      if (contentTagsError) {
        console.error('Error fetching content tags:', contentTagsError);
        return [];
      }
      
      // Group the content tags by content_id and count unique tag_ids
      const contentTagCounts: Record<string, Set<string>> = {};
      
      for (const item of contentTagsData) {
        if (!contentTagCounts[item.content_id]) {
          contentTagCounts[item.content_id] = new Set();
        }
        contentTagCounts[item.content_id].add(item.tag_id);
      }
      
      // Filter for content items that have all the specified tags
      const contentIdsWithAllTags = Object.entries(contentTagCounts)
        .filter(([_, tagSet]) => tagIds.every(tagId => tagSet.has(tagId)))
        .map(([contentId]) => contentId);
      
      console.log(`Found ${contentIdsWithAllTags.length} items with all specified tags (AND logic)`);
      
      if (contentIdsWithAllTags.length === 0) return [];
      
      // Fetch the actual content items
      const { data: contentItems, error: contentError } = await supabase
        .from('content_items')
        .select('*')
        .in('id', contentIdsWithAllTags)
        .eq('user_id', userId)
        .eq('archived', false);
      
      if (contentError) {
        console.error('Error fetching content items:', contentError);
        return [];
      }
      
      return contentItems || [];
      
    } else {
      // For OR logic, we need to find content that has ANY of the specified tags
      const { data: contentTags, error: tagsError } = await supabase
        .from('content_tags')
        .select('content_id')
        .in('tag_id', tagIds);
      
      if (tagsError) {
        console.error('Error fetching content with tags (OR):', tagsError);
        return [];
      }
      
      if (!contentTags.length) return [];
      
      // Extract unique content IDs
      const contentIds = [...new Set(contentTags.map(item => item.content_id))];
      
      console.log(`Found ${contentIds.length} items with any specified tag (OR logic)`);
      
      // Fetch the actual content items
      const { data: contentItems, error: contentError } = await supabase
        .from('content_items')
        .select('*')
        .in('id', contentIds)
        .eq('user_id', userId)
        .eq('archived', false);
      
      if (contentError) {
        console.error('Error fetching content items:', contentError);
        return [];
      }
      
      return contentItems || [];
    }
  } catch (error) {
    console.error('Error in fetchContentWithTags:', error);
    return [];
  }
};
